/**
 * Tenant Configuration API Service
 *
 * Manages tenant-specific configuration settings (admin-only)
 * - Quality gates (face detection filtering)
 * - Clustering parameters (spatial grouping)
 * - Proximity settings (indoor/outdoor thresholds)
 *
 * Note: Backend now uses unified /config endpoint that returns all sections.
 * These functions provide backward compatibility for the UI.
 * Uses httpClient for automatic 401 handling and token refresh.
 */

import { getApiEndpoint } from '../../config/tenantConfig';
import httpClient from './httpClient';

// Cache for config data to avoid multiple fetches
let configCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5000; // 5 seconds

/**
 * Get API base URL
 */
const getBaseUrl = () => {
  const endpoint = getApiEndpoint('tenantconfigapi');
  if (!endpoint) {
    throw new Error('Tenant Config API endpoint not configured. Run bin/frontend/configure-api-endpoints.sh');
  }
  return endpoint;
};

/**
 * Get all configuration (unified endpoint)
 * @returns {Promise<Object>} All config sections
 */
async function getAllConfig(useCache = true) {
  const now = Date.now();

  // Return cached data if valid
  if (useCache && configCache && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)) {
    return configCache;
  }

  try {
    const baseUrl = getBaseUrl();
    const data = await httpClient.get(`${baseUrl}/config`);

    // Update cache
    configCache = data;
    cacheTimestamp = now;

    return data;
  } catch (error) {
    console.error('Error fetching config:', error);
    throw error;
  }
}

/**
 * Update configuration (unified endpoint)
 * @param {Object} updates - Config sections to update
 * @returns {Promise<Object>} Updated config sections
 */
async function updateAllConfig(updates) {
  try {
    const baseUrl = getBaseUrl();
    const data = await httpClient.put(`${baseUrl}/config`, updates);

    // Invalidate cache
    configCache = null;
    cacheTimestamp = null;

    return data;
  } catch (error) {
    console.error('Error updating config:', error);
    throw error;
  }
}

/**
 * Get quality gates configuration
 * @returns {Promise<Object>} Quality gates config
 */
export async function getQualityGatesConfig() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.quality_gates || {};
  } catch (error) {
    console.error('Error fetching quality gates config:', error);
    throw error;
  }
}

/**
 * Update quality gates configuration
 * @param {Object} config - Quality gates settings
 * @returns {Promise<Object>} Updated config
 */
export async function updateQualityGatesConfig(config) {
  try {
    console.log('Updating quality gates config:', config);
    const result = await updateAllConfig({ quality_gates: config });
    return result.quality_gates;
  } catch (error) {
    console.error('Error updating quality gates config:', error);
    throw error;
  }
}

/**
 * Get clustering configuration
 * @returns {Promise<Object>} Clustering config
 */
export async function getClusteringConfig() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.clustering || {};
  } catch (error) {
    console.error('Error fetching clustering config:', error);
    throw error;
  }
}

/**
 * Update clustering configuration
 * @param {Object} config - Clustering settings
 * @returns {Promise<Object>} Updated config
 */
export async function updateClusteringConfig(config) {
  try {
    console.log('Updating clustering config:', config);
    const result = await updateAllConfig({ clustering: config });
    return result.clustering;
  } catch (error) {
    console.error('Error updating clustering config:', error);
    throw error;
  }
}

/**
 * Get proximity configuration
 * @returns {Promise<Object>} Proximity config
 */
export async function getProximityConfig() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.proximity || {};
  } catch (error) {
    console.error('Error fetching proximity config:', error);
    throw error;
  }
}

/**
 * Update proximity configuration
 * @param {Object} config - Proximity settings
 * @returns {Promise<Object>} Updated config
 */
export async function updateProximityConfig(config) {
  try {
    console.log('Updating proximity config:', config);
    const result = await updateAllConfig({ proximity: config });
    return result.proximity;
  } catch (error) {
    console.error('Error updating proximity config:', error);
    throw error;
  }
}

/**
 * Get compliance configuration
 * @returns {Promise<Object>} Compliance config
 */
export async function getComplianceConfig() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.compliance || {};
  } catch (error) {
    console.error('Error fetching compliance config:', error);
    throw error;
  }
}

/**
 * Update compliance configuration
 * @param {Object} config - Compliance settings
 * @returns {Promise<Object>} Updated config
 */
export async function updateComplianceConfig(config) {
  try {
    console.log('Updating compliance config:', config);
    const result = await updateAllConfig({ compliance: config });
    return result.compliance;
  } catch (error) {
    console.error('Error updating compliance config:', error);
    throw error;
  }
}

/**
 * Get visible characteristics configuration
 * @returns {Promise<Object>} Visible characteristics config
 */
export async function getVisibleCharacteristics() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.visible_characteristics || {};
  } catch (error) {
    console.error('Error fetching visible characteristics config:', error);
    throw error;
  }
}

/**
 * Update visible characteristics configuration
 * @param {Object} config - Visible characteristics settings (e.g., {Gender: true, SkinTone: false})
 * @returns {Promise<Object>} Updated config
 */
export async function updateVisibleCharacteristics(config) {
  try {
    console.log('Updating visible characteristics config:', config);
    const result = await updateAllConfig({ visible_characteristics: config });
    return result.visible_characteristics;
  } catch (error) {
    console.error('Error updating visible characteristics config:', error);
    throw error;
  }
}

/**
 * Get face matching configuration
 * @returns {Promise<Object>} Face matching config
 */
export async function getFaceMatchingConfig() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.face_matching || {};
  } catch (error) {
    console.error('Error fetching face matching config:', error);
    throw error;
  }
}

/**
 * Update face matching configuration
 * @param {Object} config - Face matching settings (known_threshold, associated_threshold, max_faces)
 * @returns {Promise<Object>} Updated config
 */
export async function updateFaceMatchingConfig(config) {
  try {
    console.log('Updating face matching config:', config);
    const result = await updateAllConfig({ face_matching: config });
    return result.face_matching;
  } catch (error) {
    console.error('Error updating face matching config:', error);
    throw error;
  }
}

/**
 * Get SOI promotion configuration
 * @returns {Promise<Object>} SOI promotion config
 */
export async function getSOIPromotionConfig() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.soi_promotion || {};
  } catch (error) {
    console.error('Error fetching SOI promotion config:', error);
    throw error;
  }
}

/**
 * Update SOI promotion configuration
 * @param {Object} config - SOI promotion settings (enabled, min_sighting_count, min_unique_days)
 * @returns {Promise<Object>} Updated config
 */
export async function updateSOIPromotionConfig(config) {
  try {
    console.log('Updating SOI promotion config:', config);
    const result = await updateAllConfig({ soi_promotion: config });
    return result.soi_promotion;
  } catch (error) {
    console.error('Error updating SOI promotion config:', error);
    throw error;
  }
}

/**
 * Get match thresholds configuration
 * @returns {Promise<Object>} Match thresholds (known, soi, associated)
 */
export async function getMatchThresholdsConfig() {
  try {
    const allConfig = await getAllConfig();
    return allConfig.match_thresholds || { known: 85, soi: 80, associated: 75 };
  } catch (error) {
    console.error('Error fetching match thresholds config:', error);
    throw error;
  }
}

/**
 * Update match thresholds configuration
 * @param {Object} config - Match thresholds settings (known, soi, associated)
 * @returns {Promise<Object>} Updated config
 */
export async function updateMatchThresholdsConfig(config) {
  try {
    console.log('Updating match thresholds config:', config);
    const result = await updateAllConfig({ match_thresholds: config });
    return result.match_thresholds;
  } catch (error) {
    console.error('Error updating match thresholds config:', error);
    throw error;
  }
}

/**
 * List all available tenants
 * @returns {Promise<Array>} Array of tenant objects {tenantId, tenantName, status}
 */
export async function listTenants() {
  try {
    const baseUrl = getBaseUrl();
    const data = await httpClient.get(`${baseUrl}/tenants`);
    return data.tenants || [];
  } catch (error) {
    console.error('Error fetching tenants:', error);
    throw error;
  }
}

